import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Star, 
  Clock, 
  Calendar, 
  Plus, 
  Check, 
  Play, 
  ExternalLink,
  ArrowLeft,
  Heart,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { getMovieDetails, getImageUrl, getWatchProviderLogo, getProviderUrl, addMovie, getUserMovies, updateMovie } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MovieDetailPage = () => {
  const { id } = useParams();
  const { isAuthenticated, token } = useAuth();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userEntry, setUserEntry] = useState(null);
  const [trailerOpen, setTrailerOpen] = useState(false);
  const [similar, setSimilar] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const movieData = await getMovieDetails(id);
        setMovie(movieData);

        // Fetch similar movies
        try {
          const similarResponse = await axios.get(`${API_URL}/api/tmdb/similar/movie/${id}`);
          setSimilar(similarResponse.data.results || []);
        } catch (e) {
          console.log('Could not fetch similar movies');
        }

        // Check if user has this movie
        if (isAuthenticated) {
          try {
            const userMovies = await getUserMovies();
            const existing = userMovies.movies?.find(m => m.tmdb_id === parseInt(id));
            setUserEntry(existing);
          } catch (e) {
            console.error('Error fetching user movies:', e);
          }
        }
      } catch (error) {
        console.error('Error fetching movie:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isAuthenticated]);

  const handleAddToWatchlist = async () => {
    if (!isAuthenticated) {
      toast.error('Log in om films toe te voegen');
      return;
    }

    try {
      const result = await addMovie({
        tmdb_id: movie.id,
        title: movie.title,
        poster_path: movie.poster_path,
        backdrop_path: movie.backdrop_path,
        status: 'watchlist',
        runtime: movie.runtime,
        genres: movie.genres?.map(g => g.name) || []
      });
      setUserEntry(result.movie);
      toast.success('Toegevoegd aan watchlist');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Film staat al in je bibliotheek');
      } else {
        toast.error('Kon film niet toevoegen');
      }
    }
  };

  const handleMarkWatched = async () => {
    if (!isAuthenticated) {
      toast.error('Log in om films te markeren');
      return;
    }

    try {
      if (userEntry) {
        // Update existing entry using the correct ID
        await axios.put(
          `${API_URL}/api/movies/${userEntry.id}`,
          { status: 'watched' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setUserEntry({ ...userEntry, status: 'watched' });
      } else {
        const result = await addMovie({
          tmdb_id: movie.id,
          title: movie.title,
          poster_path: movie.poster_path,
          backdrop_path: movie.backdrop_path,
          status: 'watched',
          runtime: movie.runtime,
          genres: movie.genres?.map(g => g.name) || []
        });
        setUserEntry(result.movie);
      }
      toast.success('Gemarkeerd als gekeken');
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Kon status niet bijwerken');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">Film niet gevonden</p>
      </div>
    );
  }

  const backdrop = getImageUrl(movie.backdrop_path, 'w1280');
  const poster = getImageUrl(movie.poster_path, 'w500');
  const watchProviders = movie.watch_providers_nl;
  const trailer = movie.videos?.results?.find(v => v.type === 'Trailer' && v.site === 'YouTube');
  
  // Get director from crew
  const director = movie.credits?.crew?.find(c => c.job === 'Director');

  return (
    <div data-testid="movie-detail-page" className="min-h-screen">
      {/* Trailer Modal */}
      <Dialog open={trailerOpen} onOpenChange={setTrailerOpen}>
        <DialogContent className="max-w-4xl p-0 bg-black border-gray-800">
          <div className="relative pt-[56.25%]">
            <button
              onClick={() => setTrailerOpen(false)}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            {trailer && (
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${trailer.key}?autoplay=1`}
                title={`${movie.title} Trailer`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Hero Section */}
      <section className="relative h-[60vh] md:h-[70vh] overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          {backdrop && (
            <img src={backdrop} alt={movie.title} className="w-full h-full object-cover" />
          )}
          {/* Lighter gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-[#050505]/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 via-[#050505]/40 to-transparent" />
        </div>

        {/* Back Button */}
        <Link 
          to="/"
          className="absolute top-4 left-4 md:top-8 md:left-8 z-20 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Terug</span>
        </Link>

        {/* Content */}
        <div className="relative h-full flex items-end">
          <div className="flex flex-col md:flex-row gap-6 md:gap-10 p-6 md:p-12 w-full max-w-6xl">
            {/* Poster */}
            <div className="hidden md:block w-64 flex-shrink-0">
              {poster && (
                <img 
                  src={poster} 
                  alt={movie.title}
                  className="w-full rounded-xl shadow-2xl ring-1 ring-white/10"
                />
              )}
            </div>

            {/* Info */}
            <div className="flex-1 max-w-2xl">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold tracking-tight text-white mb-4">
                {movie.title}
              </h1>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-300 mb-4">
                <span className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  {movie.vote_average?.toFixed(1)}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {movie.release_date?.slice(0, 4)}
                </span>
                {movie.runtime && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {Math.floor(movie.runtime / 60)}u {movie.runtime % 60}m
                  </span>
                )}
              </div>

              {/* Director */}
              {director && (
                <p className="text-sm text-gray-400 mb-4">
                  Regisseur:{' '}
                  <Link to={`/person/${director.id}`} className="text-indigo-400 hover:underline">
                    {director.name}
                  </Link>
                </p>
              )}

              {/* Genres */}
              <div className="flex flex-wrap gap-2 mb-6">
                {movie.genres?.map(genre => (
                  <Badge 
                    key={genre.id}
                    variant="secondary"
                    className="bg-white/10 text-white border-none"
                  >
                    {genre.name}
                  </Badge>
                ))}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                {trailer && (
                  <Button 
                    data-testid="watch-trailer-btn"
                    onClick={() => setTrailerOpen(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-6 py-5"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Trailer
                  </Button>
                )}

                {userEntry?.status === 'watched' ? (
                  <Button 
                    data-testid="watched-btn"
                    variant="secondary"
                    className="bg-green-600/20 text-green-400 border-green-500/30 rounded-full px-6 py-5"
                    disabled
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Gekeken
                  </Button>
                ) : (
                  <Button 
                    data-testid="mark-watched-btn"
                    variant="secondary"
                    onClick={handleMarkWatched}
                    className="bg-white/10 hover:bg-white/20 rounded-full px-6 py-5"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Markeer als gekeken
                  </Button>
                )}

                {/* Only show watchlist button if not watched and not already in watchlist */}
                {!userEntry && (
                  <Button 
                    data-testid="add-watchlist-btn"
                    variant="secondary"
                    onClick={handleAddToWatchlist}
                    className="bg-white/10 hover:bg-white/20 rounded-full px-6 py-5"
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Watchlist
                  </Button>
                )}

                {userEntry?.status === 'watchlist' && (
                  <Button 
                    data-testid="in-watchlist-btn"
                    variant="secondary"
                    className="bg-pink-600/20 text-pink-400 border-pink-500/30 rounded-full px-6 py-5"
                    disabled
                  >
                    <Heart className="w-4 h-4 mr-2 fill-current" />
                    Op watchlist
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="p-6 md:p-12 max-w-6xl">
        {/* Overview */}
        {movie.overview && (
          <div className="mb-10">
            <h2 className="text-xl font-display font-bold mb-4">Samenvatting</h2>
            <p className="text-gray-300 leading-relaxed max-w-3xl">{movie.overview}</p>
          </div>
        )}

        {/* Watch Providers */}
        {watchProviders && (watchProviders.flatrate || watchProviders.rent || watchProviders.buy) && (
          <div className="mb-10">
            <h2 className="text-xl font-display font-bold mb-4">Waar te bekijken (Nederland)</h2>
            
            <div className="space-y-4">
              {watchProviders.flatrate && (
                <div>
                  <p className="text-sm text-gray-400 mb-2">Streaming</p>
                  <div className="flex flex-wrap gap-3">
                    {watchProviders.flatrate.map(provider => (
                      <a
                        key={provider.provider_id}
                        href={getProviderUrl(provider.provider_id, watchProviders.link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                      >
                        <img 
                          src={getWatchProviderLogo(provider.logo_path)} 
                          alt={provider.provider_name}
                          className="w-8 h-8 rounded"
                        />
                        <span className="text-sm">{provider.provider_name}</span>
                        <ExternalLink className="w-3 h-3 text-gray-400" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {watchProviders.rent && (
                <div>
                  <p className="text-sm text-gray-400 mb-2">Huren</p>
                  <div className="flex flex-wrap gap-3">
                    {watchProviders.rent.map(provider => (
                      <a
                        key={provider.provider_id}
                        href={getProviderUrl(provider.provider_id, watchProviders.link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                      >
                        <img 
                          src={getWatchProviderLogo(provider.logo_path)} 
                          alt={provider.provider_name}
                          className="w-6 h-6 rounded"
                        />
                        <span className="text-sm">{provider.provider_name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cast - Full Width Scrollable */}
        {movie.credits?.cast && movie.credits.cast.length > 0 && (
          <div className="mb-10">
            <h2 className="text-xl font-display font-bold mb-4">Cast</h2>
            <div className="flex gap-4 overflow-x-auto pb-4 hide-scrollbar -mx-6 px-6 md:-mx-12 md:px-12">
              {movie.credits.cast.slice(0, 20).map(person => (
                <Link 
                  key={person.id} 
                  to={`/person/${person.id}`}
                  className="flex-shrink-0 w-24 group"
                >
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-800 mb-2 group-hover:ring-2 group-hover:ring-indigo-500 transition-all">
                    {person.profile_path ? (
                      <img 
                        src={getImageUrl(person.profile_path, 'w185')}
                        alt={person.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-600 text-2xl font-bold">
                        {person.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <p className="text-sm font-medium text-center line-clamp-1 group-hover:text-indigo-400 transition-colors">{person.name}</p>
                  <p className="text-xs text-gray-500 text-center line-clamp-1">{person.character}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Similar Movies - Full Width */}
        {similar.length > 0 && (
          <div>
            <h2 className="text-xl font-display font-bold mb-4">Vergelijkbare films</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
              {similar.slice(0, 16).map(rec => (
                <Link 
                  key={rec.id}
                  to={`/movie/${rec.id}`}
                  className="group aspect-[2/3] rounded-lg overflow-hidden bg-gray-900 hover:ring-2 hover:ring-indigo-500 transition-all"
                >
                  {rec.poster_path ? (
                    <img 
                      src={getImageUrl(rec.poster_path, 'w342')}
                      alt={rec.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center p-2">
                      <span className="text-xs text-gray-500 text-center">{rec.title}</span>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default MovieDetailPage;
